import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import api from "@/api";
import { useFinanceAuth } from "@/context/FinanceAuthContext";
import { Lock, ShieldCheck, TrendingUp, TrendingDown, Wallet, Receipt, Calendar, Key, Eye, EyeOff } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell, LineChart, Line, CartesianGrid } from "recharts";
import { toast } from "sonner";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const eur2 = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);
const COLORS = ["#ea580c", "#0f172a", "#2563eb", "#16a34a", "#eab308", "#7c3aed", "#dc2626", "#0891b2", "#db2777", "#64748b"];

// Stable references for Recharts props (avoid re-render loops from inline objects)
const AXIS_TICK_11 = { fontSize: 11 };
const AXIS_TICK_10 = { fontSize: 10 };
const AXIS_TICK_12 = { fontSize: 12 };
const LEGEND_STYLE_10 = { fontSize: 10 };
const kFormatter = (v) => `${(v / 1000).toFixed(0)}k`;
const giorniFmt = (v) => `${v}g`;
const eurTooltip = (v) => eur(v);

export default function SituazioneEconomica() {
  const { pw, unlocked, setPw, clear, verifyAndSet } = useFinanceAuth();
  const [showPw, setShowPw] = useState(false);
  const [fatturatoPrevistoInput, setFatturatoPrevistoInput] = useState("");  
  const [input, setInput] = useState("");

  const { data: status } = useQuery({
    queryKey: ["fin-status"],
    queryFn: () => api.get("/admin/situazione-economica/status").then((r) => r.data),
  });

  const verify = useMutation({
    mutationFn: (password) => verifyAndSet(password),
    onSuccess: () => {
      setInput("");
      toast.success("Area sbloccata");
    },
    onError: () => toast.error("Password errata"),
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["situazione-completa", pw],
    queryFn: () =>
      api
        .get("/admin/situazione-economica/completa", { headers: { "X-Finance-Password": pw } })
        .then((r) => r.data),
    enabled: unlocked && !!pw,
    retry: false,
  });

  const [nuovoF24, setNuovoF24] = useState({ descrizione: "", importo: "", data_scadenza: "" });

  const { data: f24 = [], refetch: aggiornaF24 } = useQuery({
    queryKey: ["scadenze-fiscali-manuali", pw],
    queryFn: () => api.get("/admin/situazione-economica/scadenze-fiscali", {
      headers: { "X-Finance-Password": pw },
    }).then(r => r.data),
    enabled: unlocked && !!pw,
  });

  const salvaF24 = useMutation({
    mutationFn: (valori) => api.post("/admin/situazione-economica/scadenze-fiscali", valori, {
      headers: { "X-Finance-Password": pw },
    }),
    onSuccess: () => {
      toast.success("F24 salvato");
      setNuovoF24({ descrizione: "", importo: "", data_scadenza: "" });
      aggiornaF24();
    },
    onError: () => toast.error("Errore nel salvataggio F24"),
  });

  const pagaF24 = useMutation({
    mutationFn: (id) => api.put(`/admin/situazione-economica/scadenze-fiscali/${id}/pagato`, {}, {
      headers: { "X-Finance-Password": pw },
    }),
    onSuccess: () => {
      toast.success("Pagamento registrato");
      aggiornaF24();
    },
    onError: () => toast.error("Errore nella registrazione del pagamento"),
  });

  const { data: fatturatoPrevistoData } = useQuery({
    queryKey: ["fatturato-annuale-previsto", pw],
    queryFn: () =>
      api.get("/admin/situazione-economica/fatturato-previsto", {
        headers: { "X-Finance-Password": pw },
      }).then((r) => r.data),
    enabled: unlocked && !!pw,
    retry: false,
  });

  const salvaFatturatoPrevisto = useMutation({
    mutationFn: (importo) =>
      api.put(
        "/admin/situazione-economica/fatturato-previsto",
        { importo },
        { headers: { "X-Finance-Password": pw } }
      ),
    onSuccess: () => {
      toast.success("Fatturato annuale previsto salvato");
      refetch();
    },
    onError: () => toast.error("Errore durante il salvataggio"),
  });
  React.useEffect(() => {
    if (fatturatoPrevistoData?.fatturato_annuale_previsto != null) {
      setFatturatoPrevistoInput(
        String(fatturatoPrevistoData.fatturato_annuale_previsto)
      );
    }
  }, [fatturatoPrevistoData]);
  if (!unlocked) {
    return (
      <div className="max-w-md mx-auto mt-10">
        <div className="bg-white border border-slate-200 p-8 shadow-brutal">
          <div className="w-14 h-14 mx-auto bg-slate-900 flex items-center justify-center mb-4">
            <Lock className="w-7 h-7 text-orange-500" />
          </div>
          <h2 className="font-display text-2xl font-black text-center mb-2">Area riservata</h2>
          <p className="text-slate-600 text-sm text-center mb-6">
            Questa pagina contiene la situazione economico-finanziaria completa dell'impresa.
            Inserisci la <b>password amministrativa</b> per accedere.
          </p>
          {status?.default_active && (
            <div className="bg-yellow-50 border border-yellow-200 p-3 text-xs text-yellow-900 mb-4">
              <b>Password iniziale predefinita:</b> <code>cantieri2026</code>
              <div>Cambiala subito dalla pagina dopo il primo accesso.</div>
            </div>
          )}
          <div className="space-y-3">
            <div className="relative">
              <input
                type={showPw ? "text" : "password"} data-no-uppercase
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && input && verify.mutate(input)}
                placeholder="Password"
                className="w-full px-3 py-3 border border-slate-300 rounded-sm text-sm pr-10"
                autoFocus
              />
              <button
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-900"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <button
              onClick={() => input && verify.mutate(input)}
              disabled={!input || verify.isPending}
              className="w-full py-3 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white font-semibold rounded-sm shadow-brutal flex items-center justify-center gap-2"
            >
              <ShieldCheck className="w-4 h-4" /> Sblocca
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || !data) return <div className="text-slate-500">Caricamento…</div>;

  const perClienteData = Object.entries(data.fatturato.per_cliente).map(([name, value]) => ({ name, value }));
  const perPeriodoData = Object.entries(data.fatturato.per_periodo).map(([mese, value]) => ({ mese, value }));
  const perCategoriaFissiData = Object.entries(data.costi.per_categoria_fissi).map(([name, value]) => ({ name, value }));

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline flex items-center gap-2"><Key className="w-3 h-3" /> Riservato · Amministratore</div>
          <h1 className="font-display text-3xl xl:text-4xl font-black">Situazione economica generale</h1>
        </div>
        <div className="flex items-center gap-2">
          <ChangePasswordButton currentPw={pw} onChanged={(np) => { setPw(np); refetch(); }} />
          <button
            onClick={() => clear()}
            className="px-3 py-2 text-xs border border-slate-300 hover:bg-slate-50 rounded-sm flex items-center gap-1"
          >
            <Lock className="w-3 h-3" /> Blocca
          </button>
        </div>
      </div>

      {/* FATTURATO */}
      <Section title="Fatturato">
        <div className="mb-5 p-4 bg-orange-50 border border-orange-200">
          <h3 className="font-bold text-slate-900 mb-2">
            Fatturato annuale aziendale previsto
          </h3>
          <p className="text-sm text-slate-600 mb-3">
            Questo importo viene utilizzato per calcolare l'incidenza
            dei costi fissi aziendali sui singoli cantieri.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={fatturatoPrevistoInput}
              onChange={(e) => setFatturatoPrevistoInput(e.target.value)}
              placeholder="Inserisci il fatturato annuale previsto"
              className="w-full sm:w-80 px-3 py-2 border border-slate-300 rounded-sm"
            />
            <button
              type="button"
              onClick={() => salvaFatturatoPrevisto.mutate(Number(fatturatoPrevistoInput))}
              disabled={
                salvaFatturatoPrevisto.isPending ||
                !Number.isFinite(Number(fatturatoPrevistoInput)) ||
                Number(fatturatoPrevistoInput) <= 0
              }
              className="px-5 py-2 bg-orange-600 text-white font-semibold rounded-sm disabled:opacity-50"
            >
              {salvaFatturatoPrevisto.isPending ? "Salvataggio..." : "Salva"}
            </button>
          </div>
          {fatturatoPrevistoData?.fatturato_annuale_previsto > 0 && (
            <p className="mt-3 text-sm text-slate-700">
              Valore attualmente salvato:{" "}
              <b>{eur2(fatturatoPrevistoData.fatturato_annuale_previsto)}</b>
            </p>
          )}
        </div>
        <div className="grid md:grid-cols-4 gap-3">
          <StatCard label="Fatturato totale" value={eur(data.fatturato.totale)} highlight />
          <StatCard label="Fatture emesse" value={eur(data.crediti_debiti.fatture_emesse)} />
          <StatCard label="Fatture incassate" value={eur(data.crediti_debiti.fatture_incassate)} />
          <StatCard label="Fatture da emettere" value={eur(data.crediti_debiti.fatture_da_emettere)} />
        </div>
        <div className="grid lg:grid-cols-2 gap-6 mt-4">
          <ChartCard title="Fatturato per cliente">
            <PieChart>
              <Pie data={perClienteData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={false}>
                {perClienteData.map((entry, i) => <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => eur(v)} />
              <Legend wrapperStyle={LEGEND_STYLE_10} />
            </PieChart>
          </ChartCard>
          <ChartCard title="Fatturato per periodo (inizio cantiere)">
            <BarChart data={perPeriodoData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="mese" tick={AXIS_TICK_11} />
              <YAxis tick={AXIS_TICK_11} tickFormatter={kFormatter} />
              <Tooltip formatter={eurTooltip} />
              <Bar dataKey="value" fill="#ea580c" />
            </BarChart>
          </ChartCard>
        </div>
        <div className="mt-4 bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr><th className="text-left p-3">Cantiere</th><th className="text-right p-3">Fatturato</th></tr>
            </thead>
            <tbody>
              {Object.entries(data.fatturato.per_cantiere).map(([n, v]) => (
                <tr key={n} className="border-t border-slate-100"><td className="p-3">{n}</td><td className="p-3 text-right font-mono">{eur(v)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* COSTI */}
      <Section title="Costi">
        <div className="grid md:grid-cols-4 lg:grid-cols-6 gap-3">
          <StatCard label="Costi totali" value={eur(data.costi.totali)} highlight />
          <StatCard label="Costi diretti" value={eur(data.costi.diretti)} />
          <StatCard label="Costi indiretti" value={eur(data.costi.indiretti)} />
          <StatCard label="Costi di gestione annuali" value={eur(data.costi.fissi_annuali)} />
          <StatCard label="Costi variabili" value={eur(data.costi.variabili)} />
          <StatCard label="Fatture fornitori" value={eur(data.costi.materiali_fornitori)} />
          <StatCard label="Stipendi (annui)" value={eur(data.costi.stipendi_annuali)} />
          <StatCard label="Leasing (annui)" value={eur(data.costi.leasing_annuali)} />
          <StatCard label="Finanziamenti (annui)" value={eur(data.costi.finanziamenti_annuali)} />
          <StatCard label="Imposte (annue)" value={eur(data.costi.imposte_annuali)} />
        </div>
        <div className="mt-4">
          <ChartCard title="Ripartizione costi fissi per categoria (annui)">
            <BarChart data={perCategoriaFissiData} layout="vertical">
              <XAxis type="number" tickFormatter={kFormatter} tick={AXIS_TICK_10} />
              <YAxis dataKey="name" type="category" width={140} tick={AXIS_TICK_10} />
              <Tooltip formatter={eurTooltip} />
              <Bar dataKey="value" fill="#0f172a" />
            </BarChart>
          </ChartCard>
        </div>
      </Section>

      {/* UTILI */}
      <Section title="Utili & margini">
        <div className="grid md:grid-cols-3 gap-3">
          <StatCard label="Utile lordo (stima)" value={fatturatoPrevistoData?.fatturato_annuale_previsto > 0 ? eur(data.utili.lordo) : "Da configurare"} />
          <StatCard label="Utile netto (stima)" value={fatturatoPrevistoData?.fatturato_annuale_previsto > 0 ? eur(data.utili.netto) : "Da configurare"} highlight />
          <StatCard label="Margine medio % (stima)" value={fatturatoPrevistoData?.fatturato_annuale_previsto > 0 ? `${data.utili.margine_medio_pct}%` : "Da configurare"} />
        </div>
      </Section>

      {/* CREDITI/DEBITI */}
      <Section title="Situazione crediti e debiti">
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
          <StatCard label="Crediti clienti" value={eur(data.crediti_debiti.crediti_clienti)} />
          <StatCard label="Debiti fornitori" value={eur(data.crediti_debiti.debiti_fornitori)} />
          <StatCard label="Fatture da incassare" value={eur(data.crediti_debiti.fatture_da_incassare)} />
          <StatCard label="Fatture fornitori" value={eur(data.crediti_debiti.fatture_fornitori)} />
          <StatCard label="Pagamenti effettuati" value={eur(data.crediti_debiti.pagamenti_effettuati)} />
          <StatCard label="Pagamenti futuri" value={eur(data.crediti_debiti.pagamenti_futuri)} />
        </div>
      </Section>

      {/* F24 INSERITI MANUALMENTE */}
      <Section title="F24 e pagamenti fiscali">
        <form className="flex flex-wrap gap-2 mb-4" onSubmit={(e) => {
          e.preventDefault();
          salvaF24.mutate({
            descrizione: nuovoF24.descrizione,
            importo: Number(nuovoF24.importo),
            data_scadenza: nuovoF24.data_scadenza,
          });
        }}>
          <input required placeholder="Descrizione F24" className="border p-2 flex-1 min-w-40"
            value={nuovoF24.descrizione}
            onChange={e => setNuovoF24({...nuovoF24, descrizione: e.target.value})} />
          <input required type="number" min="0.01" step="0.01" placeholder="Importo"
            className="border p-2 w-36" value={nuovoF24.importo}
            onChange={e => setNuovoF24({...nuovoF24, importo: e.target.value})} />
          <input required type="date" className="border p-2"
            value={nuovoF24.data_scadenza}
            onChange={e => setNuovoF24({...nuovoF24, data_scadenza: e.target.value})} />
          <button type="submit" disabled={salvaF24.isPending}
            className="bg-slate-900 text-white px-4 py-2">+ Nuovo F24</button>
        </form>
        <div className="space-y-2">
          {f24.map(s => (
            <div key={s.scadenza_id} className="border p-3 flex flex-wrap items-center justify-between gap-2">
              <span>{s.descrizione} — {eur2(s.importo)} — {s.data_scadenza}</span>
              {s.pagato ? <span className="text-green-700">Pagato</span> :
                <button className="border px-3 py-1" disabled={pagaF24.isPending}
                  onClick={() => pagaF24.mutate(s.scadenza_id)}>Segna come pagato</button>}
            </div>
          ))}
          {f24.length === 0 && <p className="text-sm text-slate-500">Nessun F24 inserito.</p>}
        </div>
      </Section>

      {/* SCADENZE FISCALI */}
      <Section title="Scadenze fiscali">
        <div className="bg-white border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left p-3">Scadenza</th>
                <th className="text-right p-3">Importo stimato</th>
                <th className="text-right p-3">Giorno mese</th>
              </tr>
            </thead>
            <tbody>
              {data.scadenze_fiscali.map((s) => (
                <tr key={s.scadenza} className="border-t border-slate-100">
                  <td className="p-3 flex items-center gap-2"><Calendar className="w-4 h-4 text-orange-500" /> {s.scadenza}</td>
                  <td className="p-3 text-right font-mono">{eur2(s.importo_stimato)}</td>
                  <td className="p-3 text-right font-mono text-xs">{s.giorno_mese}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* PREVISIONI 30/60/90/180/365 */}
      <Section title="Previsioni economico-finanziarie (30 · 60 · 90 · 180 · 365 giorni)">
        <p className="mb-3 text-sm font-semibold text-amber-800">Previsioni progressive: ogni periodo comprende i precedenti. Gli importi a 30, 60, 90, 180 e 365 giorni NON devono essere sommati tra loro.</p>
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left p-3">Orizzonte</th>
                <th className="text-right p-3">Fatturato futuro</th>
                <th className="text-right p-3">Costi futuri</th>
                <th className="text-right p-3">Utile futuro</th>
                <th className="text-right p-3">Disponibilità finanziaria</th>
                <th className="text-right p-3">Fabbisogno liquidità</th>
              </tr>
            </thead>
            <tbody>
              {data.previsioni.map((p) => (
                <tr key={p.giorni} className="border-t border-slate-100">
                  <td className="p-3 font-mono font-bold">{p.giorni} giorni</td>
                  <td className="p-3 text-right font-mono text-emerald-700">{eur(p.fatturato_futuro)}</td>
                  <td className="p-3 text-right font-mono text-red-700">{eur(p.costi_futuri)}</td>
                  <td className={`p-3 text-right font-mono font-bold ${p.utile_futuro >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {eur(p.utile_futuro)}
                  </td>
                  <td className={`p-3 text-right font-mono font-bold ${p.disponibilita_finanziaria >= 0 ? "text-slate-900" : "text-red-700"}`}>
                    {eur(p.disponibilita_finanziaria)}
                  </td>
                  <td className={`p-3 text-right font-mono ${p.fabbisogno_liquidita > 0 ? "text-red-700 font-bold" : "text-slate-400"}`}>
                    {p.fabbisogno_liquidita > 0 ? eur(p.fabbisogno_liquidita) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4">
          <ChartCard title="Andamento economico previsto">
            <LineChart data={data.previsioni}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="giorni" tick={AXIS_TICK_11} tickFormatter={giorniFmt} />
              <YAxis tick={AXIS_TICK_11} tickFormatter={kFormatter} />              <Tooltip formatter={(v) => eur(v)} />
              <Legend />
              <Line dataKey="fatturato_futuro" name="Fatturato futuro" stroke="#16a34a" strokeWidth={2} />
              <Line dataKey="costi_futuri" name="Costi futuri" stroke="#dc2626" strokeWidth={2} />
              <Line dataKey="utile_futuro" name="Utile futuro" stroke="#ea580c" strokeWidth={2} />
              <Line dataKey="disponibilita_finanziaria" name="Disponibilità" stroke="#0f172a" strokeWidth={2} />
            </LineChart>
          </ChartCard>
        </div>
      </Section>

      {/* CODICI CONTABILI — flusso automatico */}
      {data.codici_contabili && (
        <Section title="Codici contabili — quantificazione automatica">
          <div className="bg-blue-50 border border-blue-200 p-4 mb-4 text-sm text-blue-900">
            <b>Come funziona:</b> ogni <b>bolla</b>, <b>rapportino</b> (ore lavorate) e <b>costo fisso</b> viene automaticamente
            registrato con il codice del piano dei conti. Le voci qui sotto sono aggregate in tempo reale.
          </div>
          <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left p-3">Codice</th>
                  <th className="text-left p-3">Descrizione</th>
                  <th className="text-left p-3">Tipo</th>
                  <th className="text-right p-3">Bolle</th>
                  <th className="text-right p-3">Rapportini</th>
                  <th className="text-right p-3">Costi di gestione</th>
                  <th className="text-right p-3">Cantieri</th>
                  <th className="text-right p-3">Dare €</th>
                  <th className="text-right p-3">Avere €</th>
                  <th className="text-right p-3">Saldo €</th>
                </tr>
              </thead>
              <tbody>
                {data.codici_contabili.righe.map((r) => (
                  <tr key={r.codice} className="border-t border-slate-100">
                    <td className="p-3 font-mono font-bold text-orange-700">{r.codice}</td>
                    <td className="p-3">{r.descrizione}</td>
                    <td className="p-3 text-xs uppercase text-slate-500">{r.tipo?.replace(/_/g, " ")}</td>
                    <td className="p-3 text-right font-mono text-xs">{r.fonti?.bolla || 0}</td>
                    <td className="p-3 text-right font-mono text-xs">{r.fonti?.rapportino || 0}</td>
                    <td className="p-3 text-right font-mono text-xs">{r.fonti?.costo_fisso || 0}</td>
                    <td className="p-3 text-right font-mono text-xs">{r.fonti?.cantiere || 0}</td>
                    <td className="p-3 text-right font-mono">{eur(r.dare)}</td>
                    <td className="p-3 text-right font-mono">{eur(r.avere)}</td>
                    <td className={`p-3 text-right font-mono font-bold ${r.saldo >= 0 ? "text-slate-900" : "text-red-700"}`}>
                      {eur(r.saldo)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-900 text-white">
                <tr>
                  <td className="p-3 font-display font-bold" colSpan="7">TOTALE</td>
                  <td className="p-3 text-right font-mono font-bold">{eur(data.codici_contabili.totale_dare)}</td>
                  <td className="p-3 text-right font-mono font-bold">{eur(data.codici_contabili.totale_avere)}</td>
                  <td className="p-3 text-right font-mono font-black">
                    {eur(data.codici_contabili.totale_dare - data.codici_contabili.totale_avere)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-display font-bold text-xl mb-3 border-l-4 border-orange-500 pl-3">{title}</h2>
      {children}
    </div>
  );
}
function StatCard({ label, value, highlight }) {
  return (
    <div className={`bg-white border p-4 ${highlight ? "border-orange-500 shadow-brutal" : "border-slate-200"}`}>
      <div className="overline">{label}</div>
      <div className="font-display font-black text-lg xl:text-xl mt-1">{value}</div>
    </div>
  );
}
function ChartCard({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 p-4">
      <div className="overline mb-3">{title}</div>
      <div className="w-full h-72">
        <ResponsiveContainer>{children}</ResponsiveContainer>
      </div>
    </div>
  );
}

function ChangePasswordButton({ currentPw, onChanged }) {
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [np, setNp] = useState("");
  const mutation = useMutation({
    mutationFn: (payload) => api.post("/admin/situazione-economica/change-password", payload).then((r) => r.data),
    onSuccess: () => {
      toast.success("Password aggiornata");
      onChanged(np);
      setOpen(false); setCur(""); setNp("");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  return (
    <>
      <button
        onClick={() => { setCur(currentPw || ""); setOpen(true); }}
        className="px-3 py-2 text-xs border border-slate-300 hover:bg-slate-50 rounded-sm flex items-center gap-1"
      >
        <Key className="w-3 h-3" /> Cambia password
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white w-full max-w-sm border border-slate-200 p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-lg mb-4">Cambia password area riservata</h3>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Password attuale</label>
            <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm mb-3" />
            <label className="text-xs font-semibold text-slate-600 block mb-1">Nuova password (min 6)</label>
            <input type="password" value={np} onChange={(e) => setNp(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm mb-4" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="px-3 py-2 text-sm border border-slate-300 rounded-sm">Annulla</button>
              <button
                onClick={() => cur && np && mutation.mutate({ current: cur, new: np })}
                disabled={!cur || !np || np.length < 6}
                className="px-3 py-2 text-sm bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white font-semibold rounded-sm"
              >
                Salva
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
